/**
 * The answers a rep can give for "Customer Response" at check-out.
 *
 * One list, used by every check-out form. It was previously typed out inline on
 * each screen, which is how they drifted apart.
 */
export const VISIT_RESPONSES = [
    'Immediate Business',
    'Future Business',
    'Interested',
    'Not Interested',
    'Business Closed',
] as const;

/**
 * The options to show for a record whose stored answer predates this list.
 *
 * Visits already carry retired answers — 'Happy', 'Align Other partner',
 * 'Business Close' — and 'Not interested' differs from the current
 * 'Not Interested' only by a capital letter. Dropping those from the dropdown
 * would leave the field looking empty on an old visit, and a save would quietly
 * overwrite what the rep originally reported.
 *
 * So the stored value is kept as an option when the list has no match for it,
 * and an exact-case match is preferred before falling back to a case-insensitive
 * one, so 'Not interested' selects 'Not Interested' rather than being preserved
 * as a duplicate.
 */
export function visitResponseOptions(current?: string | null): string[] {
    const value = (current || '').trim();
    if (!value) return [...VISIT_RESPONSES];
    if (VISIT_RESPONSES.some(r => r === value)) return [...VISIT_RESPONSES];

    const sameIgnoringCase = VISIT_RESPONSES.find(r => r.toLowerCase() === value.toLowerCase());
    if (sameIgnoringCase) return [...VISIT_RESPONSES];

    return [...VISIT_RESPONSES, value];
}

/**
 * The value a <select> should show for a stored answer.
 *
 * Returns the canonical spelling when one matches apart from case, so the old
 * 'Not interested' rows land on 'Not Interested' instead of showing blank.
 */
export function normalisedVisitResponse(current?: string | null): string {
    const value = (current || '').trim();
    if (!value) return '';
    return VISIT_RESPONSES.find(r => r.toLowerCase() === value.toLowerCase()) || value;
}
